import React, { useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'

import { EditOrganizationDetailsModalProps } from '../OverviewDetails.types'

import { useMutation, useQueryList, useRequiredParams } from '@percent/admin-dashboard/common/hooks'
import { AcknowledgeModal, ActionModal, FormField, Modal, Select, Spacer, Text, TextInput } from '@percent/lemonade'
import { sanitizeStringToRegex } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { PatchOrganisationParams, RegistriesType, DgrStatus as DgrStatusType } from '@percent/admin-dashboard/api/types'
import { fcraIdRegExp } from '@percent/admin-dashboard/common/utility/validation'
import { FCRA_STATUS } from './EditOfficialDetailsModal.types'
import { DGR_STATUS, useGetDgrHelpers } from '@percent/admin-dashboard/common/hooks/useGetDgrHelpers/useGetDgrHelpers'

export function EditOfficialDetailsModal({ open, onClose, organization, refresh }: EditOrganizationDetailsModalProps) {
  const { t } = useTranslation()
  const { id } = useRequiredParams()
  const { dgrOptions } = useGetDgrHelpers()
  const { adminService } = useServices()
  const [errorDialogState, setErrorDialogState] = useState(false)

  const [{ isLoading, success }, { apiFunc }] = useMutation(adminService.editOrganisation, undefined, () => {
    setErrorDialogState(true)
  })

  const [{ dataOrNull }] = useQueryList(adminService.getRegistries, {
    countryCode: organization.countryCode,
    pageSize: undefined
  })

  const mapRegistries = (registries?: RegistriesType[] | null) => {
    return registries
      ? registries?.map(registry => {
          return {
            label: registry.englishName || registry.name,
            value: registry.id
          }
        })
      : []
  }

  const registriesOptions = mapRegistries(dataOrNull)
  const fcraOptions = [
    { label: t('status.yes'), value: FCRA_STATUS.REGISTERED },
    { label: t('status.no'), value: FCRA_STATUS.NOT_REGISTERED }
  ]

  const shouldRequireFcra = organization.countryCode === 'IND' && organization.types?.nonprofit === 'yes'
  const isAustralianOrganization = organization.countryCode === 'AUS'

  const getDgrDefaultValue = () => {
    if (organization.countrySpecificProperties?.DGR4) {
      return DGR_STATUS.AUS_DGR1_AND_4
    }

    if (organization.countrySpecificProperties?.DGR1) {
      return DGR_STATUS.AUS_DGR1
    }

    if (organization.countrySpecificProperties?.DGR2) {
      return DGR_STATUS.AUS_DGR2
    }

    return DGR_STATUS.NO_STATUS
  }

  const formik = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues: {
      registry: organization.registry,
      registryId: organization.registryId,
      name: organization.name,
      ...(shouldRequireFcra && {
        isFCRARegistered: organization.countrySpecificProperties?.fcra
          ? FCRA_STATUS.REGISTERED
          : FCRA_STATUS.NOT_REGISTERED,
        fcraId: organization.countrySpecificProperties?.fcra
      }),
      ...(isAustralianOrganization && {
        dgrStatus: getDgrDefaultValue()
      })
    },
    validationSchema: () =>
      Yup.lazy(value => {
        const newRegex =
          value.registry && dataOrNull?.find(registry => registry.id === value?.registry)?.registryIdFormatRegex
        const isIndianOrganization = shouldRequireFcra

        return Yup.object().shape({
          name: Yup.string()
            .max(255, t('errorMessage.orgFieldCharacterLimit', { fieldName: 'Legal name' }))
            .min(1, t('errorMessage.orgFieldCharacterLimit', { fieldName: 'Legal name' }))
            .trim()
            .required(t('errorMessage.required')),
          registry: Yup.string().required(t('errorMessage.required')),
          registryId: Yup.string()
            .max(255, t('errorMessage.orgFieldCharacterLimit', { fieldName: 'Registry ID' }))
            .min(1, t('errorMessage.orgFieldCharacterLimit', { fieldName: 'Registry ID' }))
            .trim()
            .required(t('errorMessage.required'))
            .matches(sanitizeStringToRegex(newRegex || ''), t('errorMessage.wrongFormatRegistryId')),
          ...(isIndianOrganization && {
            isFCRARegistered: Yup.string().required(t('errorMessage.required')),
            fcraId: Yup.string().when('isFCRARegistered', {
              is: FCRA_STATUS.REGISTERED,
              then: schema =>
                schema.required(t('errorMessage.required')).matches(fcraIdRegExp, t('errorMessage.wrongFormatFcraId')),
              otherwise: schema => schema.notRequired()
            })
          }),
          ...(isAustralianOrganization && {
            dgrStatus: Yup.string().required(t('errorMessage.required'))
          })
        })
      }),
    onSubmit: async ({ registry, registryId, name }) => {
      const countrySpecificData: PatchOrganisationParams['payload']['countrySpecificData'] = {}

      if (shouldRequireFcra) {
        countrySpecificData.fcra = values.isFCRARegistered === FCRA_STATUS.REGISTERED ? values.fcraId : null
      }

      if (isAustralianOrganization && values.dgrStatus) {
        if (values.dgrStatus === DGR_STATUS.NO_STATUS) {
          countrySpecificData.dgrStatusList = null
        } else {
          const splittedValues = values.dgrStatus.split(',')
          countrySpecificData.dgrStatusList = splittedValues.map(value => value.trim() as DgrStatusType)
        }
      }

      await apiFunc({
        payload: {
          registry,
          registryId,
          name,
          ...(Object.keys(countrySpecificData).length > 0 && { countrySpecificData })
        },
        id
      })
    }
  })

  const {
    errors,
    values,
    touched,
    dirty,
    handleChange,
    setFieldValue,
    setValues,
    handleBlur,
    handleSubmit,
    isValid,
    resetForm
  } = formik

  const handleCloseModal = () => {
    onClose()
    refresh()
    resetForm()
  }

  const handleOnErrorClose = () => {
    setErrorDialogState(false)
  }

  const successOrErrorModal =
    success && !errorDialogState ? (
      <AcknowledgeModal
        result="positive"
        title={t('dialog.editOrganizationDetails.success.title', { organizationCard: 'Official details' })}
        description={t('dialog.editOrganizationDetails.success.description', { organizationCard: 'official details' })}
        buttonText={t('button.close')}
        handleClose={handleCloseModal}
      />
    ) : (
      <AcknowledgeModal
        result="negative"
        title={t('dialog.editOrganizationDetails.error.title', { organizationCard: 'Official details' })}
        description={t('dialog.editOrganizationDetails.error.description', { organizationCard: 'official details' })}
        buttonText={t('button.close')}
        handleClose={handleOnErrorClose}
      />
    )

  return (
    <Modal open={open} onClose={onClose}>
      {!success && !errorDialogState ? (
        <ActionModal
          title={t('dialog.editOfficialDetails.title')}
          primaryButtonText={t('button.saveChanges')}
          secondaryButtonText={t('button.cancel')}
          type="submit"
          variant={isValid && dirty ? 'primary' : 'secondary'}
          disabled={!(isValid && dirty && !isLoading)}
          loading={isLoading}
          handleClose={() => {
            onClose()
            resetForm()
          }}
          handleSubmit={handleSubmit}
          primaryBtnTestId="official-details-submit-button"
          secondaryBtnTestId="official-details-cancel-button"
          aria-labelledby="official-details-form-modal"
        >
          <Text size="small">
            {t('dialog.editOfficialDetails.description')}
            <b> {organization.name}</b>
          </Text>
          <Spacer size={4} axis="vertical" />
          <form>
            <FormikProvider value={formik}>
              <>
                <FormField
                  label={t('dialog.addRegistry.legalNameLabel')}
                  status={touched.name && errors.name ? 'danger' : 'default'}
                  statusMessage={errors.name}
                  data-testid="legalName"
                  necessity="required"
                >
                  <TextInput
                    name="name"
                    value={values.name}
                    placeholder="Enter legal name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </FormField>
                <Spacer size={4} axis="vertical" />
                <FormField
                  label={t('dialog.approveValidationRequest.selectRegistry')}
                  status={errors.registry && touched.registry ? 'danger' : 'default'}
                  statusMessage={errors.registry}
                  data-testid="selectRegistry"
                >
                  <Select
                    placeholder={t('dialog.approveValidationRequest.selectRegistry')}
                    searchable={false}
                    options={registriesOptions}
                    defaultValue={registriesOptions.find(
                      ({ value, label }) =>
                        value === values?.registry && {
                          label,
                          value
                        }
                    )}
                    onChange={event => {
                      setFieldValue('registry', event.value)
                    }}
                  />
                </FormField>
                <Spacer size={4} axis="vertical" />
                <FormField
                  label={t('typography.registryId')}
                  data-testid="registryName"
                  status={errors.registryId && touched.registryId ? 'danger' : 'default'}
                  statusMessage={errors.registryId}
                >
                  <TextInput
                    name="registryId"
                    value={values.registryId}
                    placeholder="Enter registry id"
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </FormField>
                {shouldRequireFcra && (
                  <>
                    <Spacer size={4} axis="vertical" />
                    <FormField
                      label={t('typography.fcraRegisteredNumber')}
                      status={errors.isFCRARegistered && touched.isFCRARegistered ? 'danger' : 'default'}
                      statusMessage={errors.isFCRARegistered}
                      necessity="required"
                    >
                      <Select
                        searchable={false}
                        options={fcraOptions}
                        defaultValue={
                          values.isFCRARegistered === FCRA_STATUS.REGISTERED ? fcraOptions[0] : fcraOptions[1]
                        }
                        onChange={event => {
                          if (event.value === FCRA_STATUS.NOT_REGISTERED) {
                            setValues({
                              ...values,
                              isFCRARegistered: event.value,
                              fcraId: ''
                            })
                          } else {
                            setFieldValue('isFCRARegistered', event.value)
                          }
                        }}
                      />
                    </FormField>
                    {values.isFCRARegistered === FCRA_STATUS.REGISTERED && (
                      <>
                        <Spacer size={4} axis="vertical" />
                        <FormField
                          label={t('typography.fcraId')}
                          data-testid="fcraId"
                          status={errors.fcraId && touched.fcraId ? 'danger' : 'default'}
                          statusMessage={errors.fcraId}
                          necessity="required"
                        >
                          <TextInput
                            name="fcraId"
                            value={values.fcraId}
                            placeholder={t('typography.fcraIdPlaceholder')}
                            onBlur={handleBlur}
                            onChange={handleChange}
                          />
                        </FormField>
                      </>
                    )}
                  </>
                )}
                {isAustralianOrganization && (
                  <>
                    <Spacer size={4} axis="vertical" />
                    <FormField
                      label={t('typography.dgrStatus')}
                      data-testid="dgrStatus"
                      necessity="required"
                      status={errors.dgrStatus && touched.dgrStatus ? 'danger' : 'default'}
                      statusMessage={errors.dgrStatus}
                    >
                      <Select
                        searchable={false}
                        options={dgrOptions}
                        defaultValue={dgrOptions.find(option => option.value === values.dgrStatus)}
                        onChange={event => {
                          setFieldValue('dgrStatus', event.value)
                        }}
                      />
                    </FormField>
                  </>
                )}
              </>
            </FormikProvider>
          </form>
        </ActionModal>
      ) : (
        successOrErrorModal
      )}
    </Modal>
  )
}
